import { AxiosResponse } from 'axios';
import { ResponseError } from 'services/api/base';
import { base64ToBlob } from 'helper/downloadFile';

export const BULLETIN_WITHOUT_FILE = 'A resposta do boletim não trouxe o arquivo.';

const ehJson = (response: AxiosResponse): boolean => String(
  response.headers?.['content-type'] ?? '',
).includes('application/json');

/**
 * A rota `download-boletim/` não devolve PDF: devolve JSON com o arquivo em `data.file_b64`.
 *
 * O `prepareResponse` genérico trata "corpo é JSON com code 200" como arquivo válido — o que
 * está certo para CSV e anexos, mas aqui fazia o usuário baixar um `.pdf` com o envelope JSON
 * dentro (bug de produção de 13/08/2026, introduzido em d5f63b34e). Este preparador é
 * específico da rota justamente porque a quirk é dela, e não do download em geral.
 *
 * Erro de API sai como `ResponseError` — é o que mantém a recuperação do 403 de product_key
 * viva no `comRecuperacaoDeChave`. Corpo ilegível ou sem arquivo lança: quem chama trata como
 * falha de download (Snackbar + Sentry), em vez de salvar um arquivo corrompido em silêncio.
 */
export const prepareBulletinResponse = async (
  response: AxiosResponse,
): Promise<Blob | ResponseError> => {
  // Quando o backend passar a fazer stream de `application/pdf`, o binário já passa direto —
  // sem precisar sincronizar os dois deploys.
  if (!ehJson(response)) {
    return new Blob([response.data], { type: 'application/pdf' });
  }

  // Sem try/catch de propósito: em corpo truncado — boletins passam de 20 MB — o SyntaxError
  // do JSON.parse precisa chegar a quem chamou.
  const payload = JSON.parse(await new Blob([response.data]).text());

  if (payload.code && payload.code !== 200) {
    return { status: payload.code, data: payload.message };
  }

  const arquivoEmBase64 = payload?.data?.file_b64;

  if (typeof arquivoEmBase64 !== 'string' || !arquivoEmBase64) {
    throw new Error(BULLETIN_WITHOUT_FILE);
  }

  return base64ToBlob(arquivoEmBase64, 'application/pdf');
};

export default prepareBulletinResponse;
